'use server'

import { apiRequest, ApiResponse } from '../request'

export type MedicationControlType =
    | 'CONTROL_NONE'
    | 'CONTROL_A1'
    | 'CONTROL_A2'
    | 'CONTROL_A3'
    | 'CONTROL_B1'
    | 'CONTROL_B2'
    | 'CONTROL_C1'
    | 'CONTROL_C2'
    | 'CONTROL_C3'
    | 'CONTROL_C4'
    | 'CONTROL_C5'
    | 'CONTROL_D1'
    | 'CONTROL_D2'

export type MedicationType =
    | 'MED_GENERIC'
    | 'MED_REFERENCE'
    | 'MED_SIMILAR'
    | 'MED_MANIPULATED'
    | 'MED_BIOLOGICAL'
    | 'MED_HERBAL'
    | 'MED_SPECIFIC'
    | 'MED_OTHER'

export type StorageTemperature =
    | 'TEMP_AMBIENT'
    | 'TEMP_REFRIGERATED'
    | 'TEMP_FROZEN'
    | 'TEMP_ULTRA_FROZEN'

export type MedicationUnit =
    | 'UNIT_PILL'
    | 'UNIT_CAPSULE'
    | 'UNIT_ML'
    | 'UNIT_MG'
    | 'UNIT_G'
    | 'UNIT_AMPULE'
    | 'UNIT_VIAL'
    | 'UNIT_SACHET'
    | 'UNIT_TUBE'
    | 'UNIT_SPRAY'
    | 'UNIT_PATCH'
    | 'UNIT_SUPPOSITORY'
    | 'UNIT_DROP'
    | 'UNIT_OTHER'

export interface Medication {
    id: number
    companyId: number
    name: string
    genericName?: string
    manufacturer?: string
    anvisaRegistry?: string
    anvisaExpirationDate?: string
    barcode?: string
    type: MedicationType
    controlType: MedicationControlType
    requiresSngpc: boolean
    storageTemperature: StorageTemperature
    unit: MedicationUnit
    concentration?: string
    presentation?: string
    therapeuticClass?: string
    activeIngredients?: string[]
    minimumStock: number
    maximumStock: number
    reorderPoint: number
    isHighAlert: boolean
    storageInstructions?: string
    specialPrecautions?: string
    isActive: boolean
    deleted: boolean
    createdAt: string
    updatedAt: string
    batches?: any[]
    _count?: {
        batches: number
    }
}

export interface CreateMedicationData {
    name: string
    genericName?: string
    manufacturer?: string
    anvisaRegistry?: string
    anvisaExpirationDate?: string
    barcode?: string
    type?: MedicationType
    controlType?: MedicationControlType
    requiresSngpc?: boolean
    storageTemperature?: StorageTemperature
    unit?: MedicationUnit
    concentration?: string
    presentation?: string
    therapeuticClass?: string
    activeIngredients?: string[]
    minimumStock?: number
    maximumStock?: number
    reorderPoint?: number
    isHighAlert?: boolean
    storageInstructions?: string
    specialPrecautions?: string
}

export interface UpdateMedicationData extends Partial<CreateMedicationData> {}

export interface MedicationFilters {
    search?: string
    type?: MedicationType
    controlType?: MedicationControlType
    isActive?: boolean
    requiresSngpc?: boolean
}

// Medication CRUD
export async function getMedications(
    filters?: MedicationFilters
): Promise<ApiResponse<Medication[]>> {
    return apiRequest<Medication[]>('stock/medications', { params: filters })
}

export async function getMedication(
    id: number
): Promise<ApiResponse<Medication>> {
    return apiRequest<Medication>(`stock/medications/${id}`, {})
}

export async function createMedication(
    data: CreateMedicationData
): Promise<ApiResponse<Medication>> {
    return apiRequest<Medication>('stock/medications', { data }, 'post')
}

export async function updateMedication(
    id: number,
    data: UpdateMedicationData
): Promise<ApiResponse<Medication>> {
    return apiRequest<Medication>(`stock/medications/${id}`, { data }, 'patch')
}

export async function deleteMedication(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`stock/medications/${id}`, {}, 'delete')
}

export async function searchMedications(
    query: string
): Promise<ApiResponse<Medication[]>> {
    return apiRequest<Medication[]>('stock/medications/search', {
        params: { q: query },
    })
}

export async function getControlledMedications(): Promise<
    ApiResponse<Medication[]>
> {
    return apiRequest<Medication[]>('stock/medications/controlled', {})
}

export async function getLowStockMedications(): Promise<
    ApiResponse<Medication[]>
> {
    return apiRequest<Medication[]>('stock/medications/low-stock', {})
}

export async function getExpiringMedications(
    days?: number
): Promise<ApiResponse<Medication[]>> {
    return apiRequest<Medication[]>('stock/medications/expiring', {
        params: { days },
    })
}

// Labels
export const MEDICATION_TYPE_LABELS: Record<MedicationType, string> = {
    MED_GENERIC: 'Generico',
    MED_REFERENCE: 'Referencia',
    MED_SIMILAR: 'Similar',
    MED_MANIPULATED: 'Manipulado',
    MED_BIOLOGICAL: 'Biologico',
    MED_HERBAL: 'Fitoterapico',
    MED_SPECIFIC: 'Especifico',
    MED_OTHER: 'Outro',
}

export const MEDICATION_CONTROL_TYPE_LABELS: Record<
    MedicationControlType,
    string
> = {
    CONTROL_NONE: 'Sem Controle',
    CONTROL_A1: 'Lista A1',
    CONTROL_A2: 'Lista A2',
    CONTROL_A3: 'Lista A3',
    CONTROL_B1: 'Lista B1',
    CONTROL_B2: 'Lista B2',
    CONTROL_C1: 'Lista C1',
    CONTROL_C2: 'Lista C2',
    CONTROL_C3: 'Lista C3',
    CONTROL_C4: 'Lista C4',
    CONTROL_C5: 'Lista C5',
    CONTROL_D1: 'Lista D1',
    CONTROL_D2: 'Lista D2',
}

export const STORAGE_TEMPERATURE_LABELS: Record<StorageTemperature, string> = {
    TEMP_AMBIENT: 'Ambiente (15-30C)',
    TEMP_REFRIGERATED: 'Refrigerado (2-8C)',
    TEMP_FROZEN: 'Congelado (-25 a -15C)',
    TEMP_ULTRA_FROZEN: 'Ultra Congelado (-90 a -60C)',
}

export const MEDICATION_UNIT_LABELS: Record<MedicationUnit, string> = {
    UNIT_PILL: 'Comprimido',
    UNIT_CAPSULE: 'Capsula',
    UNIT_ML: 'mL',
    UNIT_MG: 'mg',
    UNIT_G: 'g',
    UNIT_AMPULE: 'Ampola',
    UNIT_VIAL: 'Frasco-ampola',
    UNIT_SACHET: 'Sache',
    UNIT_TUBE: 'Bisnaga',
    UNIT_SPRAY: 'Spray',
    UNIT_PATCH: 'Adesivo',
    UNIT_SUPPOSITORY: 'Supositorio',
    UNIT_DROP: 'Gota',
    UNIT_OTHER: 'Outro',
}
